'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';

/**
 * State for the ErrorBoundary component
 * @public
 */
export interface ErrorBoundaryState {
  /** Whether an error has been caught */
  hasError: boolean;
  /** The caught error object */
  error: Error | null;
  /** React error info with component stack */
  errorInfo: ErrorInfo | null;
}

/**
 * Props for the ErrorBoundary component
 * @public
 */
export interface ErrorBoundaryProps {
  /** React children to render when there's no error */
  children: ReactNode;
  /** Custom fallback UI renderer */
  fallback?: (error: Error, errorInfo: ErrorInfo, reset: () => void) => ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to isolate errors to this boundary */
  isolate?: boolean;
  /** Name for debugging purposes */
  name?: string;
}

/**
 * Error boundary component that catches JavaScript errors in child components
 * 
 * @example
 * ```tsx
 * <ErrorBoundary
 *   fallback={(error) => <div>Error: {error.message}</div>}
 *   onError={(error) => console.error(error)}
 * >
 *   <App />
 * </ErrorBoundary>
 * ```
 * 
 * @public
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`[Vibecode ErrorBoundary${this.props.name ? ` - ${this.props.name}` : ''}]:`, error, errorInfo);
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Update state with error info
    this.setState({
      errorInfo,
    });
  }

  reset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error && this.state.errorInfo) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo, this.reset);
      }

      // Default fallback UI
      return (
        <div className="vibecode-error-boundary p-4 border border-red-500 rounded-lg bg-red-50 dark:bg-red-950">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2">
            Something went wrong
          </h2>
          <details className="text-sm text-red-600 dark:text-red-400">
            <summary className="cursor-pointer mb-2">Error details</summary>
            <pre className="whitespace-pre-wrap overflow-auto p-2 bg-red-100 dark:bg-red-900 rounded">
              {this.state.error.toString()}
              {this.state.errorInfo.componentStack}
            </pre>
          </details>
          <button
            onClick={this.reset}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component that wraps a component with an error boundary
 * 
 * @param Component - Component to wrap
 * @param errorBoundaryProps - Props to pass to the ErrorBoundary
 * @returns Wrapped component
 * 
 * @example
 * ```tsx
 * const SafeComponent = withErrorBoundary(MyComponent, {
 *   onError: (error) => logError(error),
 *   fallback: (error) => <ErrorDisplay error={error} />
 * });
 * ```
 * 
 * @public
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}